import {
  ComponentType,
  useMemo,
  useCallback,
  ReactElement,
  useRef,
  PropsWithChildren,
} from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { FeedbackFlag } from '@atlaskit/feedback-collector';
import { FlagGroup } from '@atlaskit/flag';
import { EngagementMessages } from '@atlassiansox/engagekit-ts';

import { dismissFlag as dismissFlagAction } from 'src/redux/flags';
import {
  ComponentFlagId,
  ComponentFlagProps,
  FlagObject,
  SimpleFlagObject,
} from 'src/redux/flags/types';
import CommitsListUpdatedFlag from 'src/sections/global/components/flags/commits-list-updated';
import {
  CreateBranchErrorFlag,
  LoadRepositoriesErrorFlag,
} from 'src/sections/global/components/flags/create-branch/create-branch-error-flag';
import { CreateBranchSuccessFlag } from 'src/sections/global/components/flags/create-branch/create-branch-success-flag';
import { RepoMaintenanceFlag } from 'src/sections/global/components/flags/repo-maintenance/repo-maintenance-flag';
import { DefaultGroupIntroFlag } from 'src/sections/workspace/sections/settings/components/default-group-intro-flag/default-group-intro-flag';
import { BucketState } from 'src/types/state';
import {
  publishUiEvent,
  publishScreenEvent,
} from 'src/utils/analytics/publish';
import { captureMessageWithTags } from 'src/utils/sentry';

import { CompareBranchesErrorFlag } from '../components/flags/compare-branches/compare-branches-error-flag';
import { CompareBranchesSuccessFlag } from '../components/flags/compare-branches/compare-branches-success-flag';
import { CompareBranchesTimeoutFlag } from '../components/flags/compare-branches/compare-branches-timeout-flag';
import { CreateCommentErrorFlag } from '../components/flags/create-comment/create-comment-error-flag';
import CreateWorkspaceErrorFlag from '../components/flags/create-workspace/create-workspace-error-flag';
import { DefaultBranchDeletionFlag } from '../components/flags/default-branch-deletion/default-branch-deletion-flag';
import { DeleteBranchErrorFlag } from '../components/flags/delete-branch/delete-branch-error-flag';
import { DeleteBranchSuccessFlag } from '../components/flags/delete-branch/delete-branch-success-flag';
import DeleteRepositoryErrorFlag from '../components/flags/delete-repository/delete-repository-error-flag';
import DeleteRepositorySuccessFlag from '../components/flags/delete-repository/delete-repository-success-flag';
import { LoadContextErrorFlag } from '../components/flags/diff/load-context-error-flag';
import { LoadContextForbiddenNotFoundFlag } from '../components/flags/diff/load-context-forbidden-not-found-flag';
import { DiffSettingsErrorFlag } from '../components/flags/diff-settings-error-flag';
import { GiveFeedbackSuccessFlag } from '../components/flags/feedback/give-feedback-success-flag';
import { FileModeOnboardingFlag } from '../components/flags/file-mode-onboarding/file-mode-onboarding-flag';
import { InternalNewPageFlag } from '../components/flags/internal-new-page-flag/internal-new-page-flag';
import { CreateJiraIssueSuccessFlag } from '../components/flags/jira/create-jira-issue-success-flag';
import MarketingConsent from '../components/flags/marketing-consent/marketing-consent-flag';
import NonDismissibleUnlinkedWorkspaceFlag, {
  NON_DISMISSIBLE_UNLINKED_WORKSPACE_FLAG_ID,
} from '../components/flags/non-dismissible-unlinked-workspace-flag';
import OfflineFlag from '../components/flags/offline/offline';
import OnlineFlag from '../components/flags/offline/online';
import { PullRequestRetargetErrorFlag } from '../components/flags/pull-request/pull-request-retarget-error-flag';
import { PullRequestRetargetSuccessFlag } from '../components/flags/pull-request/pull-request-retarget-success-flag';
import {
  PullRequestDraftReviewers,
  PULL_REQUEST_DRAFT_REVIEWERS_FLAG_ID,
} from '../components/flags/pull-request-draft-reviewers/pull-request-draft-reviewers';
import { PullRequestUpdatedFlag } from '../components/flags/pull-request-updated-flag';
import { RevertPullRequestErrorFlag } from '../components/flags/revert-pull-request/revert-pull-request-error-flag';
import SimpleFlag from '../components/flags/simple/simple-flag';
import { SiteMessageFlag } from '../components/flags/site-message/site-message-flag';
import { SourceBranchUpdatedFlag } from '../components/flags/source-branch-updated-flag';
import UnlinkedWorkspaceFlag, {
  UNLINKED_WORKSPACE_FLAG_ID,
} from '../components/flags/unlinked-workspace-flag';
import UserNeedsAccessWarningFlag, {
  USER_NEEDS_ACCESS_FLAG_ID,
} from '../components/flags/user-needs-access';

const flagRegistry: {
  [key in ComponentFlagId]:
    | ComponentType<PropsWithChildren<ComponentFlagProps>>
    | typeof FeedbackFlag;
} = {
  'create-branch-load-repositories-error': LoadRepositoriesErrorFlag,
  'create-branch-success': CreateBranchSuccessFlag,
  'create-branch-error': CreateBranchErrorFlag,
  'create-workspace-error': CreateWorkspaceErrorFlag,
  'delete-branch-success': DeleteBranchSuccessFlag,
  'delete-branch-error': DeleteBranchErrorFlag,
  'create-comment-error': CreateCommentErrorFlag,
  'compare-branches-success': CompareBranchesSuccessFlag,
  'compare-branches-error': CompareBranchesErrorFlag,
  'compare-branches-timeout': CompareBranchesTimeoutFlag,
  'marketing-consent': MarketingConsent,
  'site-message': SiteMessageFlag,
  'diff-settings-error': DiffSettingsErrorFlag,
  'file-mode-onboarding': FileModeOnboardingFlag,
  'pull-request-updated': PullRequestUpdatedFlag,
  'source-branch-updated': SourceBranchUpdatedFlag,
  'commits-list-updated': CommitsListUpdatedFlag,
  'load-context-error': LoadContextErrorFlag,
  'load-context-forbidden-not-found': LoadContextForbiddenNotFoundFlag,
  'network-offline': OfflineFlag,
  'network-online': OnlineFlag,
  'revert-pull-request-error': RevertPullRequestErrorFlag,
  'delete-repository-success': DeleteRepositorySuccessFlag,
  'error-cannot-be-deleted-sox': DeleteRepositoryErrorFlag,
  'create-jira-issue-success': CreateJiraIssueSuccessFlag,
  'horizontal-nav-feedback-button': GiveFeedbackSuccessFlag,
  'pipelines-feedback-button': GiveFeedbackSuccessFlag,
  [USER_NEEDS_ACCESS_FLAG_ID]: UserNeedsAccessWarningFlag,
  'pull-request-retarget-success': PullRequestRetargetSuccessFlag,
  'pull-request-retarget-error': PullRequestRetargetErrorFlag,
  'default-group-intro': DefaultGroupIntroFlag,
  'internal-new-page': InternalNewPageFlag,
  'repo-maintenance-flag': RepoMaintenanceFlag,
  [PULL_REQUEST_DRAFT_REVIEWERS_FLAG_ID]: PullRequestDraftReviewers,
  'default-branch-deletion': DefaultBranchDeletionFlag,
  [UNLINKED_WORKSPACE_FLAG_ID]: UnlinkedWorkspaceFlag,
  [NON_DISMISSIBLE_UNLINKED_WORKSPACE_FLAG_ID]:
    NonDismissibleUnlinkedWorkspaceFlag,
};

const isComponentFlagId = (id: string): id is ComponentFlagId =>
  id in flagRegistry;

export function Flags() {
  const dispatch = useDispatch();
  // ref to track the flag IDs that we've dispatched screen events for
  const hasDispatchedScreenEvtForIds = useRef(new Set<string>());

  const flags = useSelector((state: BucketState) => state.flags);

  const handleDismissed = useCallback(
    (id: string) => {
      const flag = flags.find(f => f.id === id);
      if (!flag) {
        return;
      }
      publishUiEvent({
        action: 'dismissed',
        actionSubject: 'flag',
        actionSubjectId: 'bitbucketFlag',
        source: 'flag',
        attributes: {
          // use analytics flag id if passed in, otherwise default to flag id
          flag_id: flag.analyticsFlagId || flag.id,
        },
      });
      const simpleFlag = flag as SimpleFlagObject;
      if (typeof simpleFlag.onDismissed === 'function') {
        simpleFlag.onDismissed(simpleFlag.id);
      }
      dispatch(dismissFlagAction(flag.id));
    },
    [flags, dispatch]
  );

  const renderFlag = useCallback(
    (flagObj: FlagObject) => {
      // publish screen event for this flag if it hasn't been dispatched yet
      if (!hasDispatchedScreenEvtForIds.current.has(flagObj.id)) {
        publishScreenEvent('siteMessageFlag', {
          // use analytics flag id if passed in, otherwise default to flag id
          flag_id: flagObj.analyticsFlagId || flagObj.id,
        });

        // update ref to prevent dispatching screen event again
        hasDispatchedScreenEvtForIds.current.add(flagObj.id);
      }

      if (flagObj.type === 'component') {
        let FlagComponent =
          flagObj.componentId && flagRegistry[flagObj.componentId];
        if (!FlagComponent && isComponentFlagId(flagObj.id)) {
          FlagComponent = flagRegistry[flagObj.id];
        }

        if (!FlagComponent) {
          captureMessageWithTags('UNKNOWN_FLAG', { flagId: flagObj.id });

          // eslint-disable-next-line no-console
          console.warn(`UNKNOWN_FLAG: ${flagObj.id}`);
          return null;
        }

        return (
          <FlagComponent
            id={flagObj.id}
            key={flagObj.id}
            props={flagObj.props}
            handleDismissed={handleDismissed}
          />
        );
      }

      return (
        <SimpleFlag
          {...flagObj}
          key={flagObj.id}
          handleDismissed={handleDismissed}
        />
      );
    },
    [handleDismissed, hasDispatchedScreenEvtForIds]
  );

  const renderedFlags = useMemo(
    () => flags.map(renderFlag).filter((f): f is ReactElement => f !== null),
    [flags, renderFlag]
  );

  if (renderedFlags.length) {
    return <FlagGroup onDismissed={handleDismissed}>{renderedFlags}</FlagGroup>;
  } else {
    // EngagementMessages uses their own FlagGroup. If we rendered both this and the above at the same time,
    // and both were showing flags, they would overlap. So render our own first, and if there are none, render the
    // ones from engagement platform.
    return <EngagementMessages />;
  }
}
